import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { experience, education } from '../data/portfolio';

// Single timeline entry
const TimelineEntry = ({ item, index, type = 'work' }) => {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className="relative flex gap-6 md:gap-10"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${index * 100}ms, transform 0.6s ease ${index * 100}ms`,
      }}
    >
      {/* Left: year + line */}
      <div className="flex flex-col items-center" style={{ minWidth: '60px' }}>
        <div
          className="font-mono text-xs mb-2 text-center"
          style={{ color: 'var(--color-accent)' }}
        >
          {item.year || item.duration?.split(/[—-]/)[0]?.trim()}
        </div>
        <div
          className="w-px flex-1"
          style={{ background: 'var(--color-border)', minHeight: '60px' }}
        />
      </div>

      {/* Right: content */}
      <div className="pb-10 flex-1">
        <div className="section-label mb-2">{item.duration}</div>

        <h3
          className="font-bold text-lg md:text-xl mb-1"
          style={{ color: 'var(--color-text)' }}
        >
          {item.position || item.degree}
        </h3>

        <div className="flex flex-col gap-1 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-sm" style={{ color: 'var(--color-accent)' }}>
              {item.company || item.institution}
            </span>
            {item.companySubtitle && (
              <span className="font-mono text-xs" style={{ color: 'var(--color-text2)' }}>
                {item.companySubtitle}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className="font-mono text-xs"
              style={{ color: 'var(--color-text2)' }}
            >
              · {item.location}
            </span>
            {item.cgpa && (
              <span
                className="font-mono text-xs px-2 py-0.5"
                style={{
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text2)',
                }}
              >
                {item.cgpa}
              </span>
            )}
          </div>
        </div>

        {item.description && (
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--color-text2)' }}>
            {item.description}
          </p>
        )}

        {item.achievements?.length > 0 && (
          <ul className="space-y-2">
            {item.achievements.map((a, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm"
                style={{ color: 'var(--color-text2)' }}
              >
                <span style={{ color: 'var(--color-border)', flexShrink: 0 }}>—</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const Experience = () => {
  const { ref: headRef, isVisible: headVisible } = useScrollReveal({ threshold: 0.2 });

  return (
    <section
      id="experience"
      className="py-24 md:py-36"
      style={{ backgroundColor: 'var(--color-bg2)', borderTop: '1px solid var(--color-border)' }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div ref={headRef} className="mb-14 md:mb-20">
          <div
            className="section-label mb-6"
            style={{ opacity: headVisible ? 1 : 0, transition: 'opacity 0.5s ease' }}
          >
            03 / EXPERIENCE
          </div>
          <h2
            className="font-bold leading-none tracking-tight mb-2"
            style={{
              fontSize: 'clamp(2.2rem, 6vw, 5rem)',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
            }}
          >
            BUILDING THINGS
          </h2>
          <h2
            className="font-bold leading-none tracking-tight"
            style={{
              fontSize: 'clamp(2.2rem, 6vw, 5rem)',
              color: 'var(--color-text2)',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
            }}
          >
            THAT SHIP.
          </h2>
        </div>

        {/* Two-column: work + education */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Work experience */}
          <div>
            <div
              className="section-label mb-8"
              style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: '12px' }}
            >
              WORK
            </div>
            <div>
              {experience.map((job, i) => (
                <TimelineEntry key={job.id} item={job} index={i} type="work" />
              ))}
            </div>
          </div>

          {/* Education */}
          <div>
            <div
              className="section-label mb-8"
              style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: '12px' }}
            >
              EDUCATION
            </div>
            <div>
              {education.map((edu, i) => (
                <TimelineEntry key={edu.id} item={edu} index={i} type="education" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
